import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Booking from "@/pages/Booking";
import Payments from "@/pages/Payments";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentStatus";
import About from "@/pages/About";
import Testimonials from "@/pages/Testimonials";
import Podcast from "@/pages/Podcast";
import Admin from "@/pages/Admin";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const prevRef = useRef(null);
  useEffect(() => {
    // Strip the /es language prefix so a language switch on the same page keeps scroll position
    const normalize = (p) => (p === "/es" || p.startsWith("/es/") ? p.slice(3) || "/" : p);
    const isLanguageSwitch = prevRef.current !== null && normalize(prevRef.current) === normalize(pathname);
    prevRef.current = pathname;
    if (isLanguageSwitch) return;
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
};

const PAGE_ROUTES = [
  { path: "/", el: <Home /> },
  { path: "/services", el: <Services /> },
  { path: "/podcast", el: <Podcast /> },
  { path: "/booking", el: <Booking /> },
  { path: "/payments", el: <Payments /> },
  { path: "/payment/success", el: <PaymentSuccess /> },
  { path: "/payment/cancel", el: <PaymentCancel /> },
  { path: "/about", el: <About /> },
  { path: "/testimonials", el: <Testimonials /> },
  { path: "/admin", el: <Admin /> },
];

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true, anchors: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App bg-linen text-espresso font-sans min-h-screen">
      <BrowserRouter>
        <LanguageProvider>
          <ScrollToTop />
          <Navbar />
          <main>
            <Routes>
              {PAGE_ROUTES.flatMap(({ path, el }) => [
                <Route key={path} path={path} element={el} />,
                <Route key={`es-${path}`} path={path === "/" ? "/es" : `/es${path}`} element={el} />,
              ])}
              <Route path="*" element={<Home />} />
            </Routes>
          </main>
          <Footer />
          <Toaster position="top-center" richColors />
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
